import { useCallback } from 'react';
import { css } from '@emotion/react';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import { sendInvoiceReceipt } from '@frontend/api-invoices';
import { getUser } from '@frontend/auth-helpers';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { BannerNotification, CheckboxField, useFormField, Text, EmailField, currency } from '@frontend/design-system';
import { useCareCreditMakePaymentMutation } from '../../../hooks/use-care-credit-make-payment-mutation';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { getIdTypeLabel } from '../../../utils/form-option-labels';
import { CollectBaseStep } from '../atoms/step/collect-base-step';
import { CollectStepPerformanceWrapper } from '../atoms/step/collect-step-performance-wrapper';
import { CareCreditCollectMultiSteps } from '../collect-multi-step.types';
import { useCareCreditCollectContext } from '../context/care-credit-collect.context';
import { DEFAULT_FINANCE_OPTION_FALLBACK, MIN_AMOUNT_FOR_OPTIONS } from '../utils/financing-options';
import { getReceiptHtml } from './step4-receipt-options/get-receipt-html';

const CURRENT_STEP: CareCreditCollectMultiSteps = 'receipt-options';
const PREV_STEP_FINANCING: CareCreditCollectMultiSteps = 'select-financing-option';
const PREV_STEP_FORM: CareCreditCollectMultiSteps = 'payment-method-form';

const styles = {
  bannerNotification: css`
    margin-bottom: ${theme.spacing(2)};
    border-left-width: ${theme.spacing(1)};
  `,
  emailOption: css`
    padding: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
  `,
  emailOptionChecked: css`
    border: 1px solid ${theme.colors.primary50};
  `,
  emailOptionUnchecked: css`
    border: 1px solid ${theme.colors.neutral20};
  `,
  label: css`
    color: ${theme.colors.black};
  `,
  divider: css`
    margin: ${theme.spacing(2)} 0 ${theme.spacing(3)};
    border: none;
    border-top: 1px solid ${theme.colors.neutral20};
  `,
};

export const Step4ReceiptOptions = () => {
  return (
    <CollectStepPerformanceWrapper step={CURRENT_STEP}>
      <Step4ReceiptOptionsBody />
    </CollectStepPerformanceWrapper>
  );
};

const Step4ReceiptOptionsBody = () => {
  const {
    multiStep: { goPrevStep },
    selectedFinanceOption,
    careCreditData,
    invoice,
    merchant,
    person,
    isSubmitting,
    collectForm,
    locationId,
    onGoBack,
    onSubmissionComplete,
    setIsSubmitting,
    onRefetchInvoice,
  } = useCareCreditCollectContext();

  const { t } = useCareCreditTranslations();

  const user = getUser();

  const personEmail =
    person?.contactInfo?.find((contact) => contact.type === ContactType_Enum.EMAIL)?.destination || '';

  const { onChange: onChangeEmailField, ...emailFieldProps } = useFormField({
    type: 'email',
    value: personEmail,
  });

  const { onChange: onChangeCheckboxField, ...checkboxFieldProps } = useFormField({
    type: 'checkbox',
    value: !!personEmail,
  });

  const amountInDollars = Number(collectForm.fields.purchaseAmount) || (invoice?.billedAmount || 0) / 100;

  const { makePaymentMutation } = useCareCreditMakePaymentMutation({
    locationId,
    person,
    invoice,
    origin: 'LOCATION',
    selectedFinanceOption: selectedFinanceOption || DEFAULT_FINANCE_OPTION_FALLBACK,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
    // invoice may be undefined. if this is the case, the request
    // will fail validation when creating a CareCredit transaction
    invoiceId: invoice?.id || '',
  });

  const onClickSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const { paymentId: workflowId, status } = (await makePaymentMutation()) ?? {};

      if (workflowId && status === 'SUCCEEDED') {
        // completeStepTracking() -> TODO: One day add in a tracking equivalent at the collection modal navigation layer?

        const cardholderName = getFullName({
          firstName: person?.firstName,
          lastName: person?.lastName,
        });
        const dateTime = dayjs(invoice?.billedAt).format('MM/DD/YYYY h:mm A');

        if (!selectedFinanceOption?.promoCode) {
          console.warn('No promo code was found. Using static fallback values');
        }

        printFromHtml(
          getReceiptHtml({
            cardholderName,
            dateTime,
            providerName: merchant?.name || 'N/A',
            providerCompanyName: merchant?.name || 'N/A',
            accountLast4: (careCreditData?.careCreditPersonId.slice(-4) || 'N/A') + '',
            patientId: person?.personId || 'N/A',

            /**
             * If a finance option was selected, then we shouldn't use a default
             * if that field is missing. Instead show N/A.
             *
             * use fallback values when no finance option was selected.
             */
            promoCode: !!selectedFinanceOption
              ? selectedFinanceOption.promoCode || 'N/A'
              : DEFAULT_FINANCE_OPTION_FALLBACK.promoCode,
            promoTitle: !!selectedFinanceOption
              ? selectedFinanceOption.title || 'N/A'
              : DEFAULT_FINANCE_OPTION_FALLBACK.title,
            promoDescription: !!selectedFinanceOption
              ? selectedFinanceOption.description || 'N/A'
              : DEFAULT_FINANCE_OPTION_FALLBACK.description,
            amount: amountInDollars ? String(currency(amountInDollars, { decimals: 2 })) : 'N/A',
            idType: collectForm.fields.primaryIdType || 'N/A',
            expirationDate: collectForm.fields.expirationDate || 'N/A',
            idState: getIdTypeLabel(collectForm.fields.issuingState) || 'N/A',
          })
        );

        if (checkboxFieldProps.value && emailFieldProps.value) {
          sendInvoiceReceipt({
            invoiceId: invoice?.id || '',
            emails: [emailFieldProps.value],
            userId: user?.userID,
          }).catch((err) => {
            console.error('Failed to send receipt email', err);
          });
        }
        onSubmissionComplete('success');
      } else {
        onSubmissionComplete('declined');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [makePaymentMutation, emailFieldProps.value, checkboxFieldProps.value, collectForm.fields]);

  const onClickBackBtn = useCallback(() => {
    /**
     * CareCredit's alternative promos are shown for < 200 not <= 200.
     */
    goPrevStep({ orStep: amountInDollars < MIN_AMOUNT_FOR_OPTIONS ? PREV_STEP_FORM : PREV_STEP_FINANCING });
  }, [goPrevStep]);

  return (
    <CollectBaseStep
      id={CURRENT_STEP}
      label={t('Receipt Options')}
      footer={{
        primary: {
          children: t('Submit & Print Receipt'),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
        onClickPrimary: onClickSubmit,
        back: {
          children: t('Back'),
          iconName: 'back',
        },
        showBackButton: true,
        onClickBack: onClickBackBtn,
      }}
    >
      <div className='receipt-options'>
        <BannerNotification
          css={styles.bannerNotification}
          status='info'
          message={t('A printed receipt will be generated automatically.')}
        />

        <div
          className='email-option'
          css={[styles.emailOption, checkboxFieldProps.value ? styles.emailOptionChecked : styles.emailOptionUnchecked]}
        >
          <CheckboxField
            name='useEmail'
            label={
              <div>
                <Text size='large' weight='bold' css={styles.label}>
                  {t('Email Receipt (Optional)')}
                </Text>
                <Text color='subdued' size='small'>
                  {t('Send a copy of the receipt to the contact’s email')}
                </Text>
              </div>
            }
            {...checkboxFieldProps}
            onChange={onChangeCheckboxField}
          />
          <hr css={styles.divider} />

          <EmailField
            name='email'
            label={t('Email Address')}
            {...emailFieldProps}
            onChange={onChangeEmailField}
            helperText={t('A receipt will be sent to this address')}
          />
        </div>
      </div>
    </CollectBaseStep>
  );
};

const printFromHtml = (html: string) => {
  // Decode and create blob

  // Create hidden iframe
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument!;
  doc.open();
  doc.write(html);
  doc.close();

  iframe.contentWindow!.focus();
  iframe.contentWindow!.print();

  setTimeout(() => document.body.removeChild(iframe), 1000);
};
